import React, { useState, useEffect, useRef } from 'react';
import { Users, Trophy, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { basketballScoringAPI } from './services/basketballScoringApi';
import BoxScore from './BoxScore';

// Mirrors EVENT_TYPES[...].shooting on the server: the events an assist can
// legitimately attach to.
const SHOOTING_EVENTS = new Set(['free_throw', 'field_goal', 'three_pointer']);

/**
 * BasketballGameView — Read-only past game viewer
 *
 * A lightweight component that displays completed basketball game data
 * without any scoring buttons, modals, or save/autosave logic.
 *
 * Props (from PastGamesTab):
 * - gameCommonId: The game_common record ID
 * - event: The current event object
 * - groupId: The group ID
 * - members: Array of group members
 * - onBack: function() - go back
 * - onClose: function() - close
 * - onDelete: function(eventId) - delete game
 * - onGameComplete: function(result) - called on game complete
 */
const BasketballGameView = ({
  gameCommonId,
  event,
  groupId,
  members = [],
  onBack,
  onClose,
  onDelete,
  onGameComplete,
  isScorer = false,
}) => {
  const { theme, currentTheme } = useTheme();
  const isDark = currentTheme?.toLowerCase().includes('dark');

  const [loading, setLoading] = useState(true);
  const [gameSettings, setGameSettings] = useState({ team1Name: 'Team 1', team2Name: 'Team 2' });
  const [playerScores, setPlayerScores] = useState({});
  // The game's own score. Not derived from the per-player totals: scoring
  // without naming a player is allowed, so summing the box score under-reports
  // the result — a 27-16 game showed as 15-2 when most baskets were recorded
  // team-level.
  const [finalScore, setFinalScore] = useState({ a: 0, b: 0 });
  // The raw event log and the quarter line, both already on the API response.
  // Team-level facts come from these rather than from the per-player totals.
  const [possessions, setPossessions] = useState([]);
  const [periodScores, setPeriodScores] = useState([]);
  const fetchStartedRef = useRef(false);

  // Fetch game data on mount
  useEffect(() => {
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;

    const fetchGame = async () => {
      try {
        if (!gameCommonId) {
          setLoading(false);
          return;
        }

        // Read the game the same way scoring does. The old /basketball-games
        // endpoints served a stored per-player total table; that table is gone,
        // and the box score is now derived from the possession log — so a
        // completed game shows exactly the numbers that were recorded, with no
        // second copy to fall out of step.
        const game = await basketballScoringAPI.getById(gameCommonId);
        if (!game) {
          setLoading(false);
          return;
        }

        const team1Name = game.participantAInfo?.name || 'Team 1';
        const team2Name = game.participantBInfo?.name || 'Team 2';
        setGameSettings({ team1Name, team2Name, gameType: game.gameSettings?.gameType || 'friendly' });
        setFinalScore({
          a: game.basketball?.scoreA ?? game.participantAScore ?? 0,
          b: game.basketball?.scoreB ?? game.participantBScore ?? 0,
        });

        const squad = [
          ...(game.participantAInfo?.players || []).map(p => ({ ...p, teamName: team1Name })),
          ...(game.participantBInfo?.players || []).map(p => ({ ...p, teamName: team2Name })),
        ];
        const box = game.basketball?.boxScore || {};
        setPossessions(game.basketball?.possessions || []);
        setPeriodScores(game.basketball?.periodScores || []);

        const scores = {};
        Object.entries(box).forEach(([userId, s]) => {
          const who = squad.find(p => String(p.id) === String(userId));
          const name = who?.name || '';
          scores[userId] = {
            id: userId,
            userId,
            userName: name,
            firstName: name.split(' ')[0] || '',
            lastName:  name.split(' ').slice(1).join(' ') || '',
            teamName:  who?.teamName || team1Name,
            threePointersMade:   s.three_pointers_made   || 0,
            threePointersMissed: s.three_pointers_missed || 0,
            twoPointersMade:     s.two_pointers_made     || 0,
            twoPointersMissed:   s.two_pointers_missed   || 0,
            freeThrowsMade:      s.free_throws_made      || 0,
            freeThrowsMissed:    s.free_throws_missed    || 0,
            freeThrowsAttempts:  s.free_throws_attempts  || 0,
            rebounds:      s.rebounds       || 0,
            assists:       s.assists        || 0,
            steals:        s.steals         || 0,
            blocks:        s.blocks         || 0,
            personalFouls: s.personal_fouls || 0,
            technicalFouls: 0,
            turnovers:      0,
            totalPoints:   s.total_points   || 0,
          };
        });
        setPlayerScores(scores);
      } catch (error) {
        console.error('[BasketballGameView] Error fetching game:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [gameCommonId]);

  const getPlayersByTeam = (teamName) => {
    return Object.entries(playerScores)
      .filter(([_, player]) => player.teamName === teamName)
      .map(([userId, player]) => ({ ...player, userId: player.userId || userId }));
  };

  const getPlayerDisplayName = (player) => {
    if (player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`.trim();
    }
    return player.userName || 'Unknown';
  };


  /**
   * Team totals, read straight off the possession log for one side.
   *
   * Deliberately not the sum of that team's players: an action can be recorded
   * without naming who did it, and those events carry no player_id, so they are
   * absent from every per-player total. Summing the box score would report a
   * team that shot 2/4 in a game where eight other baskets went in unattributed.
   * The log has them all.
   *
   * It also picks up turnovers, which the per-player box score does not track
   * at all.
   *
   * @param {1|2} side  1 = participant A, 2 = participant B
   */
  const teamFactsFor = (side) => {
    const f = {
      fgMade: 0, fgAtt: 0, tpMade: 0, tpAtt: 0, ftMade: 0, ftAtt: 0,
      twoPoints: 0, threePoints: 0, ftPoints: 0,
      rebounds: 0, assists: 0, steals: 0, blocks: 0, turnovers: 0, fouls: 0,
      hasEvents: false,
    };

    for (const p of possessions) {
      if (p.team !== side) continue;
      // A substitution is a roster change, not a fact about how the team played
      // — counting it would make an empty game look like it had action in it.
      if (p.eventType === 'substitution') continue;
      f.hasEvents = true;

      switch (p.eventType) {
        case 'field_goal':
          f.fgAtt++;
          if (p.isMade) { f.fgMade++; f.twoPoints += p.points ?? 2; }
          break;
        case 'three_pointer':
          f.fgAtt++; f.tpAtt++;
          if (p.isMade) { f.fgMade++; f.tpMade++; f.threePoints += p.points ?? 3; }
          break;
        case 'free_throw':
          f.ftAtt++;
          if (p.isMade) { f.ftMade++; f.ftPoints += p.points ?? 1; }
          break;
        case 'rebound':  f.rebounds++;  break;
        case 'assist':   f.assists++;   break;
        case 'steal':    f.steals++;    break;
        case 'block':    f.blocks++;    break;
        case 'turnover': f.turnovers++; break;
        case 'foul':     f.fouls++;     break;
        default: break;
      }

      // An assist is normally recorded on the basket itself rather than as its
      // own row, so it is counted from there — matching how the box score does
      // it, and gated the same way so a second player on a non-shot never
      // becomes a phantom assist.
      if (p.assistPlayerId && p.isMade && SHOOTING_EVENTS.has(p.eventType)) {
        f.assists++;
      }
    }

    f.pointsOffShots = f.twoPoints + f.threePoints + f.ftPoints;
    f.attributed = getPlayersByTeam(side === 1 ? gameSettings.team1Name : gameSettings.team2Name)
      .reduce((sum, p) => sum + (p.totalPoints || 0), 0);
    return f;
  };

  // Read-only Team Column
  /** The raw per-player shape BoxScore expects, for one team. */
  const boxScoreFor = (teamName) => Object.fromEntries(
    getPlayersByTeam(teamName).map(p => [p.userId, {
      total_points:          p.totalPoints,
      two_pointers_made:     p.twoPointersMade,
      two_pointers_missed:   p.twoPointersMissed,
      three_pointers_made:   p.threePointersMade,
      three_pointers_missed: p.threePointersMissed,
      free_throws_made:      p.freeThrowsMade,
      free_throws_attempts:  p.freeThrowsAttempts,
      rebounds: p.rebounds, assists: p.assists,
      steals: p.steals, blocks: p.blocks, personal_fouls: p.personalFouls,
    }])
  );

  const TeamColumn = ({ teamName }) => {
    const isSideA = teamName === gameSettings.team1Name;

    // The team's real score, not the sum of the named players' points. Baskets
    // can be recorded without saying who scored, so the two differ whenever
    // some were — and showing the derived number here contradicted the result
    // at the top of the same page.
    const teamScore = isSideA ? finalScore.a : finalScore.b;
    const facts = teamFactsFor(isSideA ? 1 : 2);
    const quarters = periodScores.map(p => ({ period: p.period, points: isSideA ? p.a : p.b }));

    // Whoever led the team's scoring, from the named players only — it is the
    // one fact here that cannot come from the team log.
    const top = getPlayersByTeam(teamName)
      .filter(p => p.totalPoints > 0)
      .sort((a, b) => b.totalPoints - a.totalPoints)[0];

    const Split = ({ label, made, att }) => (
      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-xs ${theme.textMuted}`}>{label}</span>
        <span className={`text-sm font-semibold ${theme.text}`}>
          {made}/{att}
          <span className={`ml-1.5 text-xs font-normal ${theme.textMuted}`}>
            {att > 0 ? `${Math.round((made / att) * 100)}%` : '—'}
          </span>
        </span>
      </div>
    );

    const Stat = ({ label, value, color }) => (
      <div className="text-center">
        <p className={`text-[10px] uppercase tracking-wide ${theme.textMuted}`}>{label}</p>
        <p className={`text-xl font-bold ${color}`}>{value}</p>
      </div>
    );

    return (
      <div className={`flex-1 rounded-lg p-3 sm:p-4 ${theme.card} border ${theme.border}`}>
        {/* Team Score */}
        <div className="mb-4 text-center">
          <h2 className={`text-lg font-semibold mb-1 ${theme.textSecondary}`}>{teamName}</h2>
          <div className={`text-5xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {teamScore}
          </div>
          {facts.attributed < teamScore && (
            <p className={`mt-1 text-xs ${theme.textMuted}`}>
              {facts.attributed} from named players · {teamScore - facts.attributed} unattributed
            </p>
          )}
        </div>

        {/* A game entered as a final score has no play-by-play behind it. Say so
            once, rather than filling the card with rows of zeros that read as
            "nobody rebounded" instead of "nobody recorded it". */}
        {!facts.hasEvents ? (
          <p className={`text-xs text-center py-3 rounded ${theme.textMuted} ${theme.backgroundSecondary}`}>
            Final score only — no play-by-play recorded
          </p>
        ) : (
          <>
            {/* Quarter by quarter */}
            {quarters.length > 0 && (
              <div className={`flex flex-wrap justify-center gap-1.5 mb-3`}>
                {quarters.map(q => (
                  <span key={q.period} className={`px-2 py-1 rounded border text-xs ${theme.border} ${theme.textMuted}`}>
                    Q{q.period} <span className={`font-semibold ${theme.text}`}>{q.points}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Shooting — the numbers that explain the score */}
            <div className={`space-y-1.5 mb-3 p-3 rounded ${theme.backgroundSecondary}`}>
              <Split label="Field goals" made={facts.fgMade} att={facts.fgAtt} />
              <Split label="3-pointers"  made={facts.tpMade} att={facts.tpAtt} />
              <Split label="Free throws" made={facts.ftMade} att={facts.ftAtt} />
              {facts.pointsOffShots > 0 && (
                <p className={`pt-1.5 border-t text-[11px] ${theme.border} ${theme.textMuted}`}>
                  {facts.twoPoints} pts from 2 · {facts.threePoints} from 3 · {facts.ftPoints} from the line
                </p>
              )}
            </div>

            {/* Everything else */}
            <div className={`grid grid-cols-3 gap-2 p-3 rounded ${theme.backgroundSecondary}`}>
              <Stat label="Reb" value={facts.rebounds} color={isDark ? 'text-green-400'  : 'text-green-600'} />
              <Stat label="Ast" value={facts.assists}  color={isDark ? 'text-blue-400'   : 'text-blue-600'} />
              <Stat label="Stl" value={facts.steals}   color={isDark ? 'text-orange-400' : 'text-orange-600'} />
              <Stat label="Blk" value={facts.blocks}   color={isDark ? 'text-red-400'    : 'text-red-600'} />
              <Stat label="TO"  value={facts.turnovers} color={isDark ? 'text-gray-400'  : 'text-gray-600'} />
              <Stat label="Fouls" value={facts.fouls}  color={isDark ? 'text-amber-400'  : 'text-amber-600'} />
            </div>

            {top && (
              <p className={`mt-3 text-xs text-center ${theme.textMuted}`}>
                Top scorer <span className={`font-semibold ${theme.text}`}>{getPlayerDisplayName(top)}</span> · {top.totalPoints} pts
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={`py-4 lg:p-6 rounded-lg ${theme.background}`}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className={`text-sm ${theme.textMuted}`}>Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  const team1Score = finalScore.a;
  const team2Score = finalScore.b;

  return (
    <div className={`py-4 lg:p-6 rounded-lg ${theme.background}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text}`}>Basketball Game</h2>
          <p className={`text-sm ${theme.textMuted}`}>
            {gameSettings.team1Name} vs {gameSettings.team2Name}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
        }`}>
          Completed
        </span>
      </div>

      {/* Final Score Banner */}
      <div className={`mb-6 p-4 rounded-xl text-center ${theme.card} border ${theme.border}`}>
        <div className="flex items-center justify-center gap-6">
          <div>
            <p className={`text-sm font-medium ${theme.textSecondary}`}>{gameSettings.team1Name}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{team1Score}</p>
          </div>
          <div className={`text-2xl font-bold ${theme.textMuted}`}>-</div>
          <div>
            <p className={`text-sm font-medium ${theme.textSecondary}`}>{gameSettings.team2Name}</p>
            <p className={`text-4xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{team2Score}</p>
          </div>
        </div>
        {team1Score !== team2Score && (
          <p className={`mt-2 text-sm font-medium flex items-center justify-center gap-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
            <Trophy size={14} />
            {team1Score > team2Score ? gameSettings.team1Name : gameSettings.team2Name} wins
          </p>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <TeamColumn teamName={gameSettings.team1Name} />
        <TeamColumn teamName={gameSettings.team2Name} />
      </div>

      {/* One box score for the whole game, filterable by side. Two separate
          per-team tables could not answer "who scored most in this game",
          which is the first thing anyone looks for. */}
      <div className={`rounded-lg p-3 sm:p-4 mb-6 ${theme.card} border ${theme.border}`}>
        <h3 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${theme.textSecondary}`}>
          <Users size={16} />
          Player Stats
        </h3>
        <BoxScore
          boxScore={{ ...boxScoreFor(gameSettings.team1Name), ...boxScoreFor(gameSettings.team2Name) }}
          teams={[
            { name: gameSettings.team1Name, players: getPlayersByTeam(gameSettings.team1Name).map(p => ({ id: p.userId, name: p.userName })) },
            { name: gameSettings.team2Name, players: getPlayersByTeam(gameSettings.team2Name).map(p => ({ id: p.userId, name: p.userName })) },
          ]}
        />
      </div>


      {/* Complete Event & Continue — scorer-only, hidden once event is closed.
          Bubbles to CurrentEventTab.handleGameComplete which closes the event,
          unmounts this view, and lands the user on the empty current-event
          (Quick Schedule) view. Same path other sports use. */}
      {isScorer && onGameComplete && event?.status !== 'completed' && (
        <div className="mt-6">
          <button
            onClick={() => onGameComplete(event?.id)}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            Complete Event & Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default BasketballGameView;
